import React, { useState, useEffect } from 'react';
import { subscriptionApi } from '../../api/subscriptionApi';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PATHS } from '../../routes/path';
import { useAuth } from '../../context/AuthContext';

const featureLabels = {
  mailbox: 'Mailbox & Email',
  aiAssist: 'AI Assist (Gemini)',
  whatsapp: 'WhatsApp Reminders',
  exportReports: 'Export PDF/Excel',
  gstReports: 'GST Reports',
  invoiceCustomization: 'Invoice Customization',
  scheduledEmails: 'Scheduled Emails',
  customBranding: 'Custom Branding',
  apiAccess: 'API Access',
  multiBranch: 'Multi-branch'
};

const limitLabels = {
  maxCustomers: 'Customers',
  maxTransactions: 'Transactions/mo',
  maxProducts: 'Products',
  maxBusinesses: 'Businesses',
  maxStaff: 'Staff Accounts',
  maxAiPerDay: 'AI Calls/day',
  maxScheduledEmails: 'Scheduled Emails/day'
};

const formatLimit = (val) => {
  if (val === -1) return 'Unlimited';
  if (val === 0) return 'None';
  return val;
};

const PricingSection = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchPlans = async () => {
    try {
      const res = await subscriptionApi.getPlans();
      if (res.data.success) {
        setPlans(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  if (loading) return null;

  return (
    <div id="pricing" className="bg-gray-50 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center mb-16">
          <h2 className="text-base font-semibold leading-7 text-[#dc343b] tracking-wide uppercase">Simple Pricing</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-[2rem]">
            Choose the right plan for your business
          </p>
          <p className="mt-4 text-lg leading-8 text-gray-600">
            No hidden fees. No surprises. Start free and upgrade as you grow.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white p-1.5 rounded-full inline-flex relative border border-gray-200 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`relative px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 z-10 ${
                billingCycle === 'monthly'
                  ? 'text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {billingCycle === 'monthly' && (
                <div className="absolute inset-0 bg-[#dc343b] rounded-full -z-10 shadow-lg shadow-red-500/20"></div>
              )}
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`relative px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 z-10 ${
                billingCycle === 'yearly'
                  ? 'text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {billingCycle === 'yearly' && (
                <div className="absolute inset-0 bg-[#dc343b] rounded-full -z-10 shadow-lg shadow-red-500/20"></div>
              )}
              Yearly
              <span className="absolute -top-2 -right-3 bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                -20%
              </span>
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const isBadged = plan.badge?.text;
            const price = billingCycle === 'yearly' ? plan.price.yearly : plan.price.monthly;
            const monthlyEquivalent = billingCycle === 'yearly' ? Math.round(plan.price.yearly / 12) : plan.price.monthly;

            return (
              <div
                key={plan._id}
                className={`relative flex flex-col rounded-2xl border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  isBadged
                    ? 'border-[#dc343b] shadow-xl shadow-red-500/10 bg-white'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {/* Badge */}
                {isBadged && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-white whitespace-nowrap bg-[#dc343b]">
                    {plan.badge.text}
                  </div>
                )}

                <div className="p-6 flex-1 flex flex-col">
                  {/* Plan Name */}
                  <h3 className="text-[18px] font-extrabold text-gray-900 mb-1">{plan.name}</h3>
                  <p className="text-[12px] text-gray-500 mb-4 leading-relaxed">{plan.description}</p>

                  {/* Price */}
                  <div className="mb-6">
                    {price === 0 ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-extrabold text-gray-900">Free</span>
                        <span className="text-[13px] text-gray-500">forever</span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-[15px] font-bold text-gray-500">{plan.price.currency}</span>
                        <span className="text-4xl font-extrabold text-gray-900">{monthlyEquivalent}</span>
                        <span className="text-[13px] text-gray-500">/mo</span>
                      </div>
                    )}
                    {billingCycle === 'yearly' && price > 0 && (
                      <p className="text-[11px] text-green-600 font-semibold mt-1">
                        Billed {plan.price.currency}{price.toLocaleString('en-IN')}/year
                      </p>
                    )}
                  </div>

                  {/* Limits Highlights */}
                  <div className="space-y-2 mb-4">
                    {/* Show only top 2 limits for brevity */}
                    {['maxCustomers', 'maxBusinesses'].map(key => {
                      const val = plan.limits?.[key];
                      if (val === undefined) return null;
                      return (
                        <div key={key} className="flex items-center justify-between text-[12.5px]">
                          <span className="text-gray-600">{limitLabels[key] || key}</span>
                          <span className={`font-bold ${val === -1 ? 'text-green-600' : val === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                            {formatLimit(val)}
                          </span>
                        </div>
                      )
                    })}
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-100 mb-4"></div>

                  {/* Features Highlights */}
                  <div className="space-y-2 flex-1">
                    {/* Show only up to 4 included features */}
                    {Object.entries(plan.features || {})
                      .filter(([key, val]) => val === true && key !== 'autoBackup' && key !== 'prioritySupport')
                      .slice(0, 4)
                      .map(([key, val]) => (
                        <div key={key} className="flex items-center gap-2 text-[12.5px]">
                          <CheckIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700">
                            {featureLabels[key] || key}
                          </span>
                        </div>
                      ))}
                    
                    {/* Show "+ X more" if there are more features */}
                    {Object.entries(plan.features || {}).filter(([key, val]) => val === true && key !== 'autoBackup' && key !== 'prioritySupport').length > 4 && (
                      <div className="text-[11px] font-semibold text-[#dc343b] mt-2">
                        + {Object.entries(plan.features || {}).filter(([key, val]) => val === true && key !== 'autoBackup' && key !== 'prioritySupport').length - 4} more features
                      </div>
                    )}
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={() => user ? navigate('/pricing') : navigate(PATHS.REGISTER)}
                      className={`w-full py-3 rounded-xl text-[14px] font-bold transition-all duration-200 ${
                        plan.slug === 'premium'
                          ? 'bg-gradient-to-r from-[#dc343b] to-[#b3242a] hover:from-[#e34c52] hover:to-[#dc343b] text-white shadow-lg shadow-red-500/20'
                          : plan.slug === 'enterprise'
                            ? 'bg-gray-900 hover:bg-gray-800 text-white shadow-md'
                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200 shadow-sm border border-gray-200'
                      }`}
                    >
                      Get Started
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Compare Features Link */}
        <div className="mt-16 text-center">
          <p className="text-gray-500 mb-4 text-sm font-medium">Want to see a detailed breakdown of all features?</p>
          <button
            onClick={() => navigate('/pricing-details')}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border-2 border-gray-200 text-gray-700 font-bold hover:border-[#dc343b] hover:text-[#dc343b] transition-all bg-white shadow-sm hover:shadow-md"
          >
            Compare All Plan Details
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
